import { useRef, useState } from "react";
import { mutate } from "swr";
import { IconUpload, IconTrash, IconExternalLink, IconFileText } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Shimmer } from "../../../../components/common/shimmer";
import { purchaseOrdersService } from "../../../api_client";
import { useAuthenticatedBlob } from "../../../../hooks/useAuthenticatedBlob";
import type { OrderStatus } from "../../../../components/common/chip";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg";

interface SellerInvoiceMeta {
  id: number;
  name: string;
  mimetype: string;
  url: string;
}

interface VendorBillMeta {
  id: number;
  name: string;
  previewUrl: string | null;
}

interface Props {
  /** Used by SWR mutate after upload/remove so the page re-renders with fresh meta. */
  swrKey: string;
  purchaseOrderId: number;
  status: OrderStatus;
  /** What the seller uploaded (null until they upload). */
  sellerInvoice: SellerInvoiceMeta | null | undefined;
  /** Auto-generated vendor bill (account.move). */
  vendorBill: VendorBillMeta | null | undefined;
  /** When false, hides upload/replace/delete actions (e.g. admin view). */
  canEdit?: boolean;
}

/**
 * Two-rail invoices view for a purchase order:
 *  - "Auto-generated vendor bill" — the platform's accounting record. Read-only.
 *  - "Your tax invoice" — seller's manually uploaded PDF. Editable for the seller.
 *
 * Sellers can see this card once the order is at least delivered; uploading
 * is gated to the validated state in the parent (this card stays read-only
 * until then). Admins always see it read-only via `canEdit={false}`.
 */
export function SellerOrderInvoicesCard({
  swrKey,
  purchaseOrderId,
  status,
  sellerInvoice,
  vendorBill,
  canEdit = true,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"upload" | "delete" | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const canUpload = canEdit && status === "validated";

  async function handleUpload(file: File) {
    setBusy("upload");
    setUploadProgress(0);
    try {
      await purchaseOrdersService.uploadSellerInvoice(purchaseOrderId, file, {
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      showSuccessToast(sellerInvoice ? "Invoice replaced" : "Invoice uploaded");
      void mutate(swrKey);
    } catch (err) {
      showErrorToast(extractErrorMessage(err) || "Failed to upload invoice");
    } finally {
      setBusy(null);
      setUploadProgress(0);
    }
  }

  async function handleDelete() {
    if (!sellerInvoice) return;
    if (!window.confirm(`Remove "${sellerInvoice.name}"? You'll need to upload a new file to replace it.`)) {
      return;
    }
    setBusy("delete");
    try {
      await purchaseOrdersService.deleteSellerInvoice(purchaseOrderId);
      showSuccessToast("Invoice removed");
      void mutate(swrKey);
    } catch (err) {
      showErrorToast(extractErrorMessage(err) || "Failed to remove invoice");
    } finally {
      setBusy(null);
    }
  }

  function pickFile() {
    fileRef.current?.click();
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
      <Heading variant="h3" tone="dark">
        Invoices
      </Heading>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void handleUpload(f);
        }}
      />

      <VendorBillRow vendorBill={vendorBill ?? null} />

      <div className="border-t border-borderLight" />

      <SellerInvoiceRow
        sellerInvoice={sellerInvoice ?? null}
        purchaseOrderId={purchaseOrderId}
        canUpload={canUpload}
        canEdit={canEdit}
        busy={busy}
        uploadProgress={uploadProgress}
        onPick={pickFile}
        onDelete={handleDelete}
      />
    </div>
  );
}

function VendorBillRow({ vendorBill }: { vendorBill: VendorBillMeta | null }) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodySmBold" tone="dark">
        Auto-generated vendor bill
      </Text>
      <Text variant="bodyXs" tone="muted">
        Created automatically when the order is delivered. This is the platform's accounting record.
      </Text>
      {vendorBill ? (
        <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <IconFileText size={18} className="shrink-0 text-textMuted" />
            <Text variant="bodySmBold" tone="dark" className="truncate">
              {vendorBill.name}
            </Text>
          </div>
          {vendorBill.previewUrl ? (
            <a
              href={vendorBill.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-textBody hover:text-textDark"
            >
              <IconExternalLink size={16} />
              <Text variant="bodySm" tone="inherit">
                Open PDF
              </Text>
            </a>
          ) : (
            <Text variant="bodyXs" tone="muted">
              Not available yet
            </Text>
          )}
        </div>
      ) : (
        <Text variant="bodySm" tone="muted" className="mt-2">
          Not generated yet — produced automatically once the order is marked as delivered.
        </Text>
      )}
    </div>
  );
}

function SellerInvoiceRow({
  sellerInvoice,
  purchaseOrderId,
  canUpload,
  canEdit,
  busy,
  uploadProgress,
  onPick,
  onDelete,
}: {
  sellerInvoice: SellerInvoiceMeta | null;
  purchaseOrderId: number;
  canUpload: boolean;
  canEdit: boolean;
  busy: "upload" | "delete" | null;
  uploadProgress: number;
  onPick: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodySmBold" tone="dark">
        Your tax invoice
      </Text>
      <Text variant="bodyXs" tone="muted">
        Optional. Upload your regulator-compliant tax invoice (PDF or image). Visible to admin and the buyer.
      </Text>

      {sellerInvoice ? (
        <div className="mt-2 flex flex-col gap-2">
          <SellerInvoicePreview attachment={sellerInvoice} />
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<IconUpload size={14} />}
                onClick={onPick}
                disabled={!canUpload || busy !== null}
                title={canUpload ? undefined : "You can replace once the buyer validates the order."}
              >
                Replace
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<IconTrash size={14} />}
                onClick={onDelete}
                disabled={busy !== null}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-2">
          <Text variant="bodySm" tone="muted">
            No invoice uploaded yet.
          </Text>
          {canEdit && (
            <Button
              variant="filledDark"
              size="sm"
              leftIcon={<IconUpload size={14} />}
              onClick={onPick}
              disabled={!canUpload || busy !== null}
              title={canUpload ? undefined : "You can replace once the buyer validates the order."}
            >
              {busy === "upload" ? "Uploading…" : "Upload Invoice"}
            </Button>
          )}
          {canEdit && !canUpload && (
            <Text variant="bodyXs" tone="muted">
              Upload becomes available after the buyer confirms receipt.
            </Text>
          )}
        </div>
      )}

      {busy === "upload" && (
        <div className="mt-2 flex items-center justify-center rounded-xl border border-borderDefault bg-textDark/60 px-4 py-3">
          <Text variant="bodySmBold" className="text-bgWhite">{uploadProgress}%</Text>
        </div>
      )}

      {/* Hidden — the parent owns the input ref so we can share the picker. */}
      <input type="hidden" data-purchase-order-id={purchaseOrderId} />
    </div>
  );
}

/**
 * Renders an inline preview for image invoices and a "filename + Open"
 * affordance for PDFs. Same auth-gated proxy pattern we use for delivery
 * proof — fetch the bytes through the api instance (carries the JWT) and
 * render a blob: URL.
 */
function SellerInvoicePreview({ attachment }: { attachment: SellerInvoiceMeta }) {
  const { src, error } = useAuthenticatedBlob(attachment.url);
  const isImage = attachment.mimetype.startsWith("image/");

  if (error) {
    return (
      <div className="flex h-24 items-center justify-center rounded-xl bg-bgMuted">
        <Text variant="bodyXs" tone="muted">Couldn't load {attachment.name}</Text>
      </div>
    );
  }

  if (!src) {
    return <Shimmer className="h-24 w-full rounded-xl" />;
  }

  if (isImage) {
    return (
      <div className="mt-2 flex flex-col gap-2">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="block h-32 overflow-hidden rounded-xl border border-borderDefault bg-bgLight"
        >
          <img src={src} alt={attachment.name} className="h-full w-full object-contain" />
        </a>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <IconFileText size={18} className="shrink-0 text-textMuted" />
            <Text variant="bodySmBold" tone="dark" className="truncate">
              {attachment.name}
            </Text>
          </div>
          <a
            href={src}
            download={attachment.name}
            className="inline-flex items-center gap-1 text-textBody hover:text-textDark"
          >
            <IconExternalLink size={16} />
            <Text variant="bodySm" tone="inherit">Download</Text>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <IconFileText size={18} className="shrink-0 text-textMuted" />
        <Text variant="bodySmBold" tone="dark" className="truncate">
          {attachment.name}
        </Text>
      </div>
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-textBody hover:text-textDark"
      >
        <IconExternalLink size={16} />
        <Text variant="bodySm" tone="inherit">
          Open
        </Text>
      </a>
    </div>
  );
}

function extractErrorMessage(err: unknown): string | null {
  const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(", ") : msg ?? null;
}
